import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Heading — typographic primitive.
 *
 * `as` controls the rendered tag; `size` controls visual scale independently.
 * `tone='display'` switches to the Fraunces serif for marketing surfaces.
 */

type Tag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type Size = 'display-xl' | 'display-lg' | 'display-md' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const sizeClasses: Record<Size, string> = {
  'display-xl': 'text-7xl font-medium tracking-tighter leading-none',
  'display-lg': 'text-6xl font-medium tracking-tighter leading-none',
  'display-md': 'text-5xl font-medium tracking-tight leading-none',
  h1: 'text-4xl font-semibold tracking-tight leading-tight',
  h2: 'text-3xl font-semibold tracking-tight leading-tight',
  h3: 'text-2xl font-semibold leading-snug',
  h4: 'text-xl font-semibold leading-snug',
  h5: 'text-lg font-semibold leading-normal',
  h6: 'text-base font-semibold leading-normal',
};

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: Tag;
  size?: Size;
  tone?: 'default' | 'display';
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ as: Comp = 'h2', size, tone = 'default', className, ...props }, ref) => {
    const finalSize: Size = size ?? (Comp as Size);
    const isDisplay = tone === 'display' || finalSize.startsWith('display-');
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-text-primary text-balance',
          sizeClasses[finalSize],
          isDisplay ? 'font-display' : 'font-sans',
          className,
        )}
        {...props}
      />
    );
  },
);
Heading.displayName = 'Heading';
